import { initializeApp } from "firebase/app";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import {
  getFunctions,
  connectFunctionsEmulator,
  httpsCallable,
} from "firebase/functions";
import { getStorage } from "firebase/storage";
import { getAnalytics } from "firebase/analytics";

// Your web app's Firebase configuration
// IMPORTANT: Replace these with your actual Firebase project config
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "YOUR-API-KEY",
  authDomain:
    process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ||
    "YOUR-PROJECT.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "YOUR-PROJECT-ID",
  storageBucket:
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "YOUR-PROJECT.appspot.com",
  messagingSenderId:
    process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "YOUR-SENDER-ID",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "YOUR-APP-ID",
  measurementId:
    process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "YOUR-MEASUREMENT-ID",
};

console.log(firebaseConfig, "firebaseConfig");

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app);
export const storage = getStorage(app);

// Initialize Analytics only in production
let analytics = null;
if (process.env.NODE_ENV === "production") {
  analytics = getAnalytics(app);
}
export { analytics };

// Connect emulators for local development (opt-out via REACT_APP_USE_FIREBASE_EMULATORS=false)
const useEmulators =
  process.env.NODE_ENV === "development" &&
  process.env.REACT_APP_USE_FIREBASE_EMULATORS !== "false";

if (useEmulators) {
  connectAuthEmulator(auth, "http://localhost:9099", { disableWarnings: true });
  connectFirestoreEmulator(db, "localhost", 8080);
  connectFunctionsEmulator(functions, "localhost", 5001);
}

// Helper function to call Firebase Functions
export const callFunction = async (functionName, data = {}) => {
  try {
    const func = httpsCallable(functions, functionName);
    const result = await func(data);
    return result.data;
  } catch (error) {
    console.error(`Error calling function ${functionName}:`, error);
    throw error;
  }
};

// Collection references
export const collections = {
  users: "users",
  games: "games",
  boards: "boards",
  entries: "entries",
  winners: "winners",
  trivia: "trivia",
  accessTokens: "accessTokens",
  squareClaims: "squareClaims",
  notifications: "notifications",
  payouts: "payouts",
  referrals: "referrals",
  triviaAttempts: "triviaAttempts",
};

// Board configuration (matches backend)
export const TIERS = {
  TIER_10: {
    id: "tier_10",
    amount: 10,
    name: "Promo",
    color: "bg-teal-500",
    prizePerQuarter: 80,
    totalPrize: 400,
    description: "Win $80 per quarter!",
    championPrize: 80,
  },
  TIER_25: {
    id: "tier_25",
    amount: 25,
    name: "Entry",
    color: "bg-green-600",
    prizePerQuarter: 200,
    totalPrize: 1000,
    description: "Win $200 per quarter!",
    championPrize: 200,
  },
  TIER_50: {
    id: "tier_50",
    amount: 50,
    name: "Core",
    color: "bg-blue-600",
    prizePerQuarter: 450,
    totalPrize: 2250,
    description: "Win $450 per quarter!",
    championPrize: 450,
  },
  TIER_100: {
    id: "tier_100",
    amount: 100,
    name: "Competitive",
    color: "bg-purple-600",
    prizePerQuarter: 900,
    totalPrize: 4500,
    description: "Win $900 per quarter!",
    championPrize: 900,
  },
  TIER_250: {
    id: "tier_250",
    amount: 250,
    name: "Pro",
    color: "bg-yellow-600",
    prizePerQuarter: 2250,
    totalPrize: 11250,
    description: "Win $2,250 per quarter!",
    championPrize: 2250,
  },
  TIER_500: {
    id: "tier_500",
    amount: 500,
    name: "Elite",
    color: "bg-red-600",
    prizePerQuarter: 4500,
    totalPrize: 22500,
    description: "Win $4,500 per quarter!",
    championPrize: 4500,
  },
  TIER_1000: {
    id: "tier_1000",
    amount: 1000,
    name: "Champion",
    color: "bg-gray-800",
    prizePerQuarter: 9000,
    totalPrize: 45000,
    description: "Win $9,000 per quarter!",
    championPrize: 9000,
  },
};

// Game configuration
export const GAME_CONFIG = {
  SQUARES_PER_BOARD: 50,
  MAX_PLAYERS_PER_SQUARE: 2,
  MAX_SQUARES_PER_USER: 3,
  TRIVIA_TIME_LIMIT: 7, // seconds
  TOKEN_EXPIRY_HOURS: 48,
  CONSOLATION_TOKEN_EXPIRY_DAYS: 30,
};

// Instant payout configuration
export const INSTANT_PAYOUT_FEE = 0.08; // 8%

// Helper to get board by ID
export const getTierById = (tierId) => {
  return Object.values(TIERS).find((tier) => tier.id === tierId) || null;
};

// Helper to format currency
export const formatCurrency = (amount) => {
  if (isNaN(amount)) {
    return "$0";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

export default app;
